/**
 * FilterSearch Component
 *
 * A flexible, feature-rich search filter component built with MUI 5.18.0.
 * Allows users to build complex search queries using interactive filter chips.
 *
 * Features:
 * - Multiple filter types (text, number, date)
 * - Sub-options for conditional filtering (e.g., "more than", "less than")
 * - Immediate-apply sub-options for quick selections
 * - Default search filters for quick text search
 * - Initial filters for pre-populated state
 * - Keyboard navigation (Enter, Backspace, Escape)
 *
 * @example
 * <FilterSearch
 *   filterOptions={[
 *     { key: "name", label: "Name" },
 *     { key: "price", label: "Price", type: "number", subOptions: [...] }
 *   ]}
 *   onSearch={(filters) => console.log(filters)}
 *   defaultSearchFilters={["name", "email"]}
 *   initialFilters={[{ key: "status", label: "Status", value: "active" }]}
 * />
 */

"use client";

import React from "react";
import { Box, Paper, InputBase, ClickAwayListener } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
// Internal imports
import { ActiveFilter, FilterSearchProps } from "./types";
import { DEFAULT_FILTERS, DEFAULT_PLACEHOLDER } from "./constants";
import { useFilterSearch } from "./hooks/use-filter-search";
import { FilterChip, FilterDropdown, DatePickerDropdown } from "./components";

/**
 * FilterSearch is the main component for building search queries with filters.
 *
 * @param props - Component props (see FilterSearchProps interface)
 * @returns Rendered filter search component
 */
export default function FilterSearch({
  filterOptions = DEFAULT_FILTERS,
  handleSearch,
  placeholder = DEFAULT_PLACEHOLDER,
  defaultSearchFilters,
  initialFilters,
}: FilterSearchProps) {
  // ============================================
  // Hook for state management and logic
  // ============================================
  const {
    // State
    activeFilters,
    currentFilter,
    inputValue,
    showDropdown,
    dropdownMode,
    isFocused,
    datePickerOpen,
    selectedDate,
    availableFilters,
    isDateFilter,

    // Refs
    containerRef,
    inputRef,

    // Handlers
    handleContainerClick,
    handleFilterSelect,
    handleSubOptionSelect,
    handleInputChange,
    handleKeyDown,
    handleRemoveFilter,
    handleClearCurrentFilter,
    handleClickAway,
    handleDateChange,
    handleDateAccept,
    setIsFocused,

    // Utilities
    getChipLabel,
    getCurrentChipLabel,
    canEnterValue,
  } = useFilterSearch({
    filterOptions,
    onSearch: (val) => {
      const filters: Record<string, ActiveFilter> = {};
      val.forEach((filter) => {
        filters[filter.key] = filter;
      });
      handleSearch(filters);
    },
    defaultSearchFilters,
    initialFilters,
  });

  // ============================================
  // Handler for cancelling date picker
  // ============================================
  const handleDatePickerCancel = () => {
    handleClearCurrentFilter();
  };

  // ============================================
  // Handler for returning to filter list
  // ============================================
  const handleBackToFilters = () => {
    handleClearCurrentFilter();
  };

  // ============================================
  // Render
  // ============================================
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ width: "100%" }}>
          {/* Main input container */}
          <Paper
            ref={containerRef}
            onClick={handleContainerClick}
            elevation={isFocused ? 2 : 1}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              p: "8px",
              minHeight: 56,
              cursor: "text",
              border: "1px solid",
              borderColor: isFocused ? "primary.main" : "divider",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              backgroundColor: "#f2f2f2",
              "&:hover": {
                borderColor: isFocused ? "primary.main" : "text.secondary",
              },
            }}
          >
            {/* Search icon */}
            <SearchIcon sx={{ color: "text.secondary", mr: 0.5 }} />

            {/* Active filter chips */}
            {activeFilters.map((filter) => (
              <FilterChip
                key={filter.key}
                label={getChipLabel(filter)}
                onDelete={() => handleRemoveFilter(filter.key)}
                variant="active"
              />
            ))}

            {/* Current filter chip (being edited) */}
            {currentFilter && (
              <FilterChip
                label={getCurrentChipLabel()}
                onDelete={handleClearCurrentFilter}
                variant="current"
                isWaitingForSubOption={Boolean(currentFilter.subOptions) && !canEnterValue()}
              />
            )}

            {/* Text input for value entry */}
            <InputBase
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              placeholder={
                activeFilters.length === 0 && !currentFilter
                  ? placeholder
                  : currentFilter && canEnterValue()
                  ? "Type value..."
                  : ""
              }
              type={currentFilter?.inputType || "text"}
              inputProps={{
                inputMode: currentFilter?.inputType === "number" ? "numeric" : "text",
              }}
              sx={{
                flex: 1,
                minWidth: currentFilter ? 80 : 150,
                fontSize: 14,
                "& input": {
                  p: 0,
                },
                // Hide number input spinners for cleaner UI
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                  {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
              }}
            />
          </Paper>

          {/* Filter/Sub-option dropdown */}
          <FilterDropdown
            open={showDropdown}
            anchorEl={containerRef.current}
            mode={dropdownMode}
            availableFilters={availableFilters}
            currentFilter={currentFilter}
            onFilterSelect={handleFilterSelect}
            onSubOptionSelect={handleSubOptionSelect}
            onBackToFilters={handleBackToFilters}
          />

          {/* Date picker dropdown (for date-type filters) */}
          <DatePickerDropdown
            open={datePickerOpen && isDateFilter}
            anchorEl={containerRef.current}
            filterLabel={currentFilter?.label}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onDateAccept={handleDateAccept}
            onCancel={handleDatePickerCancel}
          />
        </Box>
      </ClickAwayListener>
    </LocalizationProvider>
  );
}
